import React, { Component } from 'react';
import { Loader, Dimmer } from 'semantic-ui-react';
import { CONFIG } from '../../helper/config';

interface IProps {
    active_loader: boolean;
    loading_time: number
};

interface IState {
    active: boolean;
}


/**
 * `LoaderComponent` is a reusable loading indicator that ensures the loader
 * remains visible for at least a given duration (`loading_time`) to prevent
 * flickering when content loads too quickly.
 *
 * Features:
 * - Shows a Semantic UI `Dimmer` + `Loader` overlay.
 * - Ensures the loader remains visible for the specified minimum time.
 * - Cleans up pending timers on unmount to avoid memory leaks.
 *
 * Props:
 * - `active_loader` → Whether the loader should be active.
 * - `loading_time` → Minimum duration (ms) the loader should remain visible.
 *
 * State:
 * - `active` → Internal flag for loader visibility, synced with `active_loader` and time logic.
 *
 * Example usage:
 * ```tsx
 * <LoaderComponent active_loader={isLoading} loading_time={1000} />
 * ```
 */
export default class LoaderComponent extends Component<IProps, IState> {

    /** Timeout reference used for managing delayed loader deactivation. */
    interval: any;

    /** Timestamp (ms) when the loader was activated. */
    start_timer: number;
    constructor(props: IProps) {
        super(props);
        this.state = {
            active: props.active_loader
        }
        this.interval = null;
        this.start_timer = 0;
    }

    /**
     * componentDidUpdate is called whenever the component is re-rendered
     * Updates the state of the loader whenever the active_loader prop changes
     * If active_loader is true, it starts a timer and sets the start_timer to the current time
     * If active_loader is false, it calculates the difference between the current time and the start time
     * If the difference is greater than or equal to the loading_time, it sets the start_timer to 0 and updates the state
     * If the difference is not 0, it sets a timeout for the remaining time and updates the state when the timeout is reached
     */
    componentDidUpdate() {
        let { active_loader, loading_time } = this.props;
        if (active_loader && this.start_timer === 0) {
            if (this.interval) {
                clearTimeout(this.interval);
            }
            if (this.state.active !== active_loader) {
                this.setState({ active: active_loader });
            }
            this.start_timer = new Date().getTime();
        } else if (!active_loader && this.start_timer !== 0) {
            let difference = new Date().getTime() - this.start_timer;
            if (difference >= loading_time) {
                this.start_timer = 0;
                this.setState({ active: active_loader });
            } else if (difference !== 0) {
                if (this.interval) {
                    clearTimeout(this.interval);
                }
                this.interval = setTimeout(() => {
                    this.start_timer = 0;
                    this.setState({ active: active_loader });
                }, (loading_time - difference));
            }
        }
    }

    /**
     * Cleanup lifecycle method.
     * Clears any active timeout when the component is unmounted
     * to prevent state updates on an unmounted component.
     */
    componentWillUnmount() {
        if (this.interval) {
            clearTimeout(this.interval);
        }
    }

    /**
     * Renders the loader wrapped inside a Semantic UI `Dimmer`.
     * Loader is centered and styled to overlay the entire screen.
     */
    render() {
        return (
            <Dimmer data-testid="loader"  active={this.state.active} style={{ zIndex: '9999', position: 'fixed' }}>
                <Loader inline='centered' size='huge' >{CONFIG.LOADER_TEXT}</Loader>
            </Dimmer>
        );
    }
}
